import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {compareForUpdate, UpdateObject} from '@/helpers/compareForUpdate';
import {ContactInformation} from '@/interfaces/contactInformation.interface';
import {ContactInformationTemplateDto} from '@/classes/dto/_common/templates/ContactInformation.template.dto';

/** Keys of the contact information that can carry a note */
export type ContactNoteKey = 'mail1' | 'mail2' | 'phone1' | 'phone2' | 'mobile1' | 'mobile2';
export type ContactValueKey = ContactNoteKey | 'fax1' | 'fax2' | 'website';

/** Plain, always complete form state of a contact information (empty string = not set) */
export type ContactFormValue = Record<ContactValueKey, string> & { notes: Record<ContactNoteKey, string> };

export const CONTACT_NOTE_KEYS: ContactNoteKey[] = ['mail1', 'mail2', 'phone1', 'phone2', 'mobile1', 'mobile2'];
export const CONTACT_VALUE_KEYS: ContactValueKey[] = [...CONTACT_NOTE_KEYS, 'fax1', 'fax2', 'website'];

export function emptyContactForm(): ContactFormValue {
    return contactFormFromEntity(undefined);
}

/** Converts a stored contact information into the plain form state */
export function contactFormFromEntity(source: ContactInformation | ContactInformationTemplateDto | undefined | null): ContactFormValue {
    const notes = (source?.notes || {}) as Partial<Record<ContactNoteKey, string | null>>;
    const form = {notes: {}} as ContactFormValue;
    CONTACT_VALUE_KEYS.forEach((key) => {
        form[key] = (source && source[key]) || '';
    });
    CONTACT_NOTE_KEYS.forEach((key) => {
        form.notes[key] = notes[key] || '';
    });
    return form;
}

/** Create payload, same shape as the former createContactInformation component emitted */
export function contactFormToCreateDto(form: ContactFormValue): ContactInformationTemplateDto {
    return {
        fax1: form.fax1 || undefined,
        fax2: form.fax2 || undefined,
        mail1: form.mail1 || undefined,
        mail2: form.mail2 || undefined,
        phone1: form.phone1 || undefined,
        phone2: form.phone2 || undefined,
        mobile1: form.mobile1 || undefined,
        mobile2: form.mobile2 || undefined,
        website: form.website || undefined,
        notes: {
            mail1: form.notes.mail1 || undefined,
            mail2: form.notes.mail2 || undefined,
            mobile1: form.notes.mobile1 || undefined,
            mobile2: form.notes.mobile2 || undefined,
            phone1: form.notes.phone1 || undefined,
            phone2: form.notes.phone2 || undefined,
        },
    };
}

/**
 * Vuetify rule for a website, close to the server check (class-validator IsUrl: protocol optional, http/https/ftp, top level domain).
 * Empty is valid.
 */
export function websiteRule(value: string | null | undefined): true | string {
    const message = 'Bitte eine gültige Website eingeben, z. B. https://muster.de';
    const v = (value || '').trim();
    if (!v) {
        return true;
    }
    if (/\s/.test(v)) {
        return message;
    }
    const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`;
    try {
        const url = new URL(withProtocol);
        const validProtocol = ['http:', 'https:', 'ftp:'].includes(url.protocol);
        const hasTld = /\.([a-z]{2,}|xn--[a-z0-9-]+)$/i.test(url.hostname);
        return (validProtocol && hasTld) || message;
    } catch (e) {
        return message;
    }
}

/** International format of a (German) phone number as it will be stored, undefined if it can not be parsed */
export function toInternationalPhone(value: string | undefined | null): string | undefined {
    if (!value) {
        return undefined;
    }
    return parsePhoneNumberFromString(value, 'DE')?.formatInternational() || undefined;
}

/**
 * Update payload (only changed props, null = delete), same logic as the former editContactInformation component:
 * phone and mobile numbers are compared and sent in international format. Returns undefined without changes.
 */
export function contactFormToUpdateDto(form: ContactFormValue, original: ContactInformation | undefined | null): UpdateObject<ContactInformation> | undefined {
    const old = (original || {notes: {}}) as ContactInformation;
    const oldNotes = old.notes || {};
    const updateObj = {} as UpdateObject<ContactInformation>;
    updateObj.fax1 = compareForUpdate(form.fax1 || undefined, old.fax1);
    updateObj.fax2 = compareForUpdate(form.fax2 || undefined, old.fax2);
    updateObj.mail1 = compareForUpdate(form.mail1 || undefined, old.mail1);
    updateObj.mail2 = compareForUpdate(form.mail2 || undefined, old.mail2);
    updateObj.phone1 = compareForUpdate(toInternationalPhone(form.phone1), old.phone1);
    updateObj.phone2 = compareForUpdate(toInternationalPhone(form.phone2), old.phone2);
    updateObj.mobile1 = compareForUpdate(toInternationalPhone(form.mobile1), old.mobile1);
    updateObj.mobile2 = compareForUpdate(toInternationalPhone(form.mobile2), old.mobile2);
    updateObj.website = compareForUpdate(form.website || undefined, old.website);
    const notes: Record<string, string | null | undefined> = {};
    CONTACT_NOTE_KEYS.forEach((key) => {
        notes[key] = compareForUpdate(form.notes[key], oldNotes[key]);
    });
    const hasNotes = Object.keys(notes).some((key) => notes[key] !== undefined);
    updateObj.notes = hasNotes ? notes as unknown as ContactInformation['notes'] : undefined;
    const hasProps = Object.keys(updateObj).some((key) => (updateObj as Record<string, unknown>)[key] !== undefined);
    return hasProps ? updateObj : undefined;
}
